import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
    FaComments,
    FaChartLine,
    FaCalendarAlt,
    FaDollarSign,
    FaPercentage,
    FaRegClock,
    FaShareAlt,
    FaTrashAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import CampaignChatModal from "../CampaignTracker/CampaignChatModal";

const CampaignTable = ({ user, onRowClick }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [, setWindowWidth] = useState(window.innerWidth);
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isWithdrawing, setIsWithdrawing] = useState(false);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const userId = user?.id;
                if(!userId) return;
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/accounts/entrepreneur/${userId}/campaigns`,
                    {
                        credentials: "include",
                    },
                );
                const data = await res.json();
                if(!Array.isArray(data)) return;
                setCampaigns(
                    data.map((item) => ({
                        id: item.id,
                        name: item.name,
                        dateSubmitted: item.dateSubmitted || null,
                        status: item.status,
                        raised: item.raised || 0,
                        percentageRaised: item.percentageRaised || 0,
                        daysRemaining: item.daysRemaining || 0,
                        goal: item.goal || 1,
                        fundingBegins: item.fundingBegins || null,
                        submitted:
                            item.submitted !== undefined ? item.submitted : 1,
                        withdrawal_requested: !!item.withdrawal_requested,
                    })),
                );
            } catch(error) {
                console.error("Error fetching campaigns:", error);
            }
        };
        fetchCampaigns();
    }, [user, isWithdrawing]);

    const handleWithdrawCampaign = async (campaignId) => {
        setIsWithdrawing(true);
        try {
            const result = await Swal.fire({
                title: "Request Campaign Withdrawal?",
                text: "This will send a withdrawal request to the admin for approval. Your campaign will remain active until approved.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, request withdrawal!",
                cancelButtonText: "Cancel",
            });

            if(result.isConfirmed) {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/withdraw`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            entrepreneurId: user?.id,
                        }),
                        credentials: "include",
                    },
                );

                if(response.ok) {
                    Swal.fire(
                        "Request Submitted!",
                        "Your withdrawal request has been sent to the admin for approval.",
                        "success",
                    );
                    const updatedCampaigns = campaigns.map((campaign) =>
                        campaign.id === campaignId
                            ? { ...campaign, withdrawal_requested: true }
                            : campaign,);
                    setCampaigns(updatedCampaigns);
                } else {
                    const errorData = await response.json();
                    Swal.fire(
                        "Error!",
                        errorData.error || "Failed to submit withdrawal request",
                        "error",
                    );
                }
            }
        } catch(error) {
            console.error("Error requesting withdrawal:", error);
            Swal.fire("Error!", "An unexpected error occurred", "error");
        } finally {
            setIsWithdrawing(false);
        }
    };

    // Just use all campaigns since pagination is removed
    const filteredCampaigns = campaigns;

    // Function to get status color based on status ENUM('pending','approved','rejected','onhold','withdrawn')
    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case "approved":
                return "bg-green-100 text-green-600";
            case "pending":
                return "bg-amber-100 text-amber-600";
            case "onhold":
                return "bg-blue-100 text-blue-600";
            case "rejected":
                return "bg-red-100 text-red-600";
            case "withdrawn":
            case "closed":
            case "ended":
                return "bg-gray-100 text-gray-600";
            case "completed":
                return "bg-emerald-100 text-emerald-700";
            default:
                return "bg-gray-100 text-gray-600";
        }
    };

    const getStatusBorderColor = (status) => {
        switch(status?.toLowerCase()) {
            case "approved":
                return "border-green-400";
            case "pending":
                return "border-amber-200";
            case "onhold":
                return "border-blue-200";
            case "rejected":
                return "border-red-200";
            case "withdrawn":
            case "closed":
            case "ended":
                return "border-gray-200";
            case "completed":
                return "border-emerald-300";
            default:
                return "border-gray-200";
        }
    };

    // Pagination removed

    return (
        <div className="flex flex-col">
            <div className="mb-6">
                <div className="border-b border-gray-200 mb-4">
                    <div className="flex items-center pb-2">
                        <FaChartLine className="text-navy mr-2 text-xl" />
                        <h1 className="text-2xl font-bold text-navy">
                            YOUR CAMPAIGNS
                        </h1>
                    </div>
                </div>
            </div>

            {filteredCampaigns.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-10 text-center border border-gray-100 my-8">
                    <div className="text-5xl text-gray-300 mb-5">
                        <FaChartLine className="inline-block" />
                    </div>
                    <p className="text-2xl text-gray-500 mb-3">
                        No Campaigns Found
                    </p>
                    <p className="text-gray-400 mb-8 max-w-md mx-auto">
                        Start your fundraising journey by creating and
                        submitting a campaign!
                    </p>
                    <button
                        onClick={() =>
                            (window.location.href = "/campaign-page")
                        }
                        className="px-6 py-3 bg-navy text-white rounded-md hover:bg-[#003557] transition-colors duration-200 font-medium"
                    >
                        Create New Campaign
                    </button>
                </div>
            ) : (
                <div className="flex flex-col space-y-6 mb-4">
                    {filteredCampaigns.map((campaign) => (
                        <div
                            key={campaign.id}
                            className={`bg-white rounded-lg shadow-sm overflow-hidden border-l-4 ${getStatusBorderColor(
                                campaign.status,
                            )} ${
                                campaign.status?.toLowerCase() !== "withdrawn" && !campaign.withdrawal_requested
                                    ? "hover:shadow-lg hover:border-l-[6px]"
                                    : ""
                            } transition-all duration-200 relative group`}
                            onClick={() =>
                                campaign.status?.toLowerCase() !== "withdrawn" &&
                                !campaign.withdrawal_requested &&
                                onRowClick &&
                                onRowClick(campaign)
                            }
                            style={{
                                cursor:
                                    campaign.status?.toLowerCase() !== "withdrawn" && !campaign.withdrawal_requested
                                        ? "pointer"
                                        : "default",
                            }}
                        >
                            <div
                                className={`absolute inset-0 bg-gray-400 ${
                                    campaign.status?.toLowerCase() !== "withdrawn" && !campaign.withdrawal_requested
                                        ? "bg-opacity-0 group-hover:bg-opacity-5"
                                        : "bg-opacity-0"
                                } transition-all duration-200 pointer-events-none`}
                            ></div>
                            <div
                                className={`absolute top-0 left-0 right-0 bg-gray-100 py-1.5 text-center text-xs text-gray-600 font-medium border-b border-gray-100 ${
                                    campaign.status?.toLowerCase() !== "withdrawn" && !campaign.withdrawal_requested
                                        ? "opacity-0 group-hover:opacity-100"
                                        : "hidden"
                                } transition-opacity duration-200`}
                            >
                                Click on the card to go to the campaign page
                            </div>
                            <div className="p-6 pt-8">
                                <div className="flex justify-between items-center mb-5">
                                    <h3
                                        className="font-semibold text-xl text-navy"
                                        title={campaign.name}
                                    >
                                        {campaign.name}
                                        {campaign.status?.toLowerCase() ===
                                            "withdrawn" && (
                                            <span className="ml-2 text-xs text-gray-500 font-normal">
                                                (Not available)
                                            </span>
                                        )}
                                    </h3>
                                    <div className="flex items-center space-x-2">
                                        {campaign.withdrawal_requested && (
                                            <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                                                Withdrawal Pending
                                            </span>
                                        )}
                                        <span
                                            className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(
                                                campaign.status,
                                            )}`}
                                        >
                                            {campaign.status}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-8 mb-6">
                                    {campaign.dateSubmitted && (
                                        <div className="flex items-center">
                                            <div className="mr-3">
                                                <FaCalendarAlt className="text-gray-400 text-lg" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">
                                                    SUBMITTED
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    {new Date(
                                                        campaign.dateSubmitted,
                                                    ).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {campaign.raised !== undefined && (
                                        <div className="flex items-center">
                                            <div className="mr-3">
                                                <FaDollarSign className="text-gray-400 text-lg" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">
                                                    RAISED
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    $
                                                    {parseFloat(
                                                        campaign.raised,
                                                    ).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {campaign.percentageRaised !==
                                        undefined && (
                                        <div className="flex items-center">
                                            <div className="mr-3">
                                                <FaPercentage className="text-gray-400 text-lg" />
                                            </div>
                                            <div className="w-full">
                                                <p className="text-xs text-gray-500 font-medium">
                                                    PROGRESS
                                                </p>
                                                <div className="flex items-center">
                                                    <p className="text-sm text-gray-700 mr-2">
                                                        {
                                                            campaign.percentageRaised
                                                        }
                                                        %
                                                    </p>
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                                        <div
                                                            className="bg-navy h-full"
                                                            style={{
                                                                width: `${Math.min(
                                                                    campaign.percentageRaised,
                                                                    100,
                                                                )}%`,
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {campaign.fundingBegins && (
                                        <div className="flex items-center">
                                            <div className="mr-3">
                                                <FaRegClock className="text-gray-400 text-lg" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">
                                                    FUNDING BEGINS
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    {new Date(
                                                        campaign.fundingBegins,
                                                    ).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                                    <button
                                        className="text-sm text-navy hover:text-[#003557] font-medium flex items-center"
                                        onClick={(e) => {
                                            e.stopPropagation(); /* Placeholder for share distribution functionality */
                                        }}
                                    >
                                        <FaShareAlt className="mr-2" /> Share
                                        Distribution
                                    </button>

                                    <div className="flex items-center space-x-2">
                                        {campaign.status === "approved" && !campaign.withdrawal_requested && (
                                            <button
                                                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center hover:bg-red-50 py-1 px-2 rounded"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleWithdrawCampaign(
                                                        campaign.id,
                                                    );
                                                }}
                                            >
                                                <FaTrashAlt className="mr-2" />{" "}
                                                Withdraw Campaign
                                            </button>
                                        )}

                                        <button
                                            className="text-sm text-[#b8860b] hover:text-[#e3b23c] font-medium flex items-center hover:bg-amber-50 py-1 px-2 rounded"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCampaign(campaign);
                                                setChatModalOpen(true);
                                            }}
                                        >
                                            <FaComments className="mr-2" /> Chat
                                            with Investors
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {chatModalOpen && selectedCampaign && (
                <CampaignChatModal
                    open={chatModalOpen}
                    onClose={() => {
                        setChatModalOpen(false);
                        setSelectedCampaign(null);
                    }}
                    campaignId={selectedCampaign.id}
                    entrepreneurId={user?.id}
                    entrepreneurUser={user}
                />
            )}
        </div>
    );
};

CampaignTable.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    onRowClick: PropTypes.func.isRequired,
};

export default CampaignTable;
